<template>
  <div ref="outer_wrapper" style="position:relative">
  <template v-if="!is_vertical">
  <div class="serra-mappa-wrapper">
    <svg
      ref="svg_el"
      :viewBox="`0 0 ${SVG_W} ${svgHeight}`"
      width="100%"
      class="serra-mappa-svg"
      @mousemove="onSvgMouseMove"
      @mouseleave="hover_fila_id = null"
    >
      <defs>
        <!-- Grigliato sfondo serra -->
        <pattern
          :id="`grid-${serra.id}`"
          x="0" y="0" width="7" height="7"
          patternUnits="userSpaceOnUse"
        >
          <path d="M 7 0 L 0 0 0 7" fill="none"
                :stroke="tipo_colori.stroke" stroke-width="1" stroke-opacity="0.22"/>
        </pattern>

        <!-- Pattern colore varietà (mono-varietà) -->
        <pattern
          v-for="info in pattern_list"
          :key="info.pattern_id"
          :id="info.pattern_id"
          x="0" y="0" width="1" height="1"
          patternContentUnits="objectBoundingBox"
        >
          <image
            :href="info.img_url"
            x="0" y="0" width="1" height="1"
            preserveAspectRatio="xMidYMid slice"
          />
        </pattern>
        <!-- ClipPath settori per pattern torta (multi-varietà) -->
        <clipPath
          v-for="sec in pie_sector_list"
          :key="sec.cp_id"
          :id="sec.cp_id"
          clipPathUnits="objectBoundingBox"
        >
          <path :d="sec.path_d"/>
        </clipPath>
        <!-- Pattern torta per prodotti multi-varietà -->
        <pattern
          v-for="pie in pie_pattern_list"
          :key="pie.pattern_id"
          :id="pie.pattern_id"
          x="0" y="0" width="1" height="1"
          patternContentUnits="objectBoundingBox"
        >
          <g v-for="sec in pie.sectors" :key="`s-${sec.cp_id}`">
            <image v-if="sec.img_url" :href="sec.img_url" x="0" y="0" width="1" height="1"
                   :clip-path="`url(#${sec.cp_id})`" preserveAspectRatio="xMidYMid slice"/>
            <path v-else :d="sec.path_d" :fill="sec.fallback"/>
          </g>
        </pattern>
      </defs>

      <!-- Sfondo cornice serra -->
      <rect
        v-if="file_ordinate.length"
        :x="FRAME_X"
        :y="PAD_Y"
        :width="FRAME_W"
        :height="frame_height"
        :fill="tipo_colori.fill"
        :stroke="tipo_colori.stroke"
        stroke-width="2"
        rx="8"
      />
      <!-- Grigliato sovrapposto -->
      <rect
        v-if="file_ordinate.length"
        :x="FRAME_X"
        :y="PAD_Y"
        :width="FRAME_W"
        :height="frame_height"
        :fill="`url(#grid-${serra.id})`"
        rx="8"
        pointer-events="none"
      />

      <!-- Titolo serra in alto nella cornice -->
      <text
        v-if="file_ordinate.length"
        :x="FRAME_X + FRAME_W / 2"
        :y="PAD_Y - 4"
        text-anchor="middle"
        dominant-baseline="auto"
        class="serra-title"
        :style="{ fill: tipo_colori.stroke }"
      >{{ serra.nome }}</text>

      <!-- Striscia avanserra (sinistra, verticale) -->
      <g v-if="show_avanserra && file_ordinate.length">
        <path :d="avanserra_path_d" fill="#D5C9A8" />
        <text
          :transform="`rotate(-90, ${FRAME_X + 1 + (FRAME_PAD + AVANSERRA_W - 1) / 2}, ${PAD_Y + frame_height / 2})`"
          :x="FRAME_X + 1 + (FRAME_PAD + AVANSERRA_W - 1) / 2"
          :y="PAD_Y + frame_height / 2"
          text-anchor="middle"
          dominant-baseline="middle"
          class="avanserra-label"
        >avanserra</text>
      </g>

      <!-- File (righe) -->
      <g
        v-for="(fila, i) in file_ordinate"
        :key="'fila-' + fila.id"
      >
        <!-- Label fila -->
        <text
          :x="FRAME_X - 6"
          :y="rowY(i) + filaH(fila.id) / 2"
          text-anchor="end"
          dominant-baseline="middle"
          class="fila-label"
          :style="{ fill: tipo_colori.stroke }"
        >{{ fila.numero }}</text>

        <!-- Sfondo della riga (rotaia) -->
        <rect
          :x="TRACK_X"
          :y="rowY(i)"
          :width="TRACK_W"
          :height="filaH(fila.id)"
          :fill="track_fill"
          opacity="0"
          rx="3"
        />

        <!-- Vasi singoli -->
        <circle
          v-for="vi in fila.num_vasi"
          :key="'v-' + vi"
          :cx="potCx(fila, vi)"
          :cy="rowY(i) + filaH(fila.id) / 2"
          :r="potRadius(fila)"
          :fill="cellPatternId(fila.id, vi) ? `url(#${cellPatternId(fila.id, vi)})` : cellColor(fila.id, vi)"
          :stroke="cellStroke(fila.id, vi)"
          :stroke-width="pot_stroke_w"
        >
          <title>{{ cellTooltip(fila.id, vi) }}</title>
        </circle>

        <!-- Contatore vasi (fuori dalla cornice, a destra) -->
        <text
          :x="FRAME_X + FRAME_W + COUNTER_W / 2"
          :y="rowY(i) + filaH(fila.id) / 2"
          text-anchor="middle"
          dominant-baseline="middle"
          class="vasi-count"
        >{{ fila.num_vasi }}</text>

        <!-- Corridoio dopo questa fila -->
        <template v-if="fila.corridoio_dopo">
          <rect
            :x="TRACK_X"
            :y="rowY(i) + filaH(fila.id) + ROW_GAP"
            :width="TRACK_W"
            :height="CORRIDOIO_INNER_H"
            fill="#D5C9A8"
          />
          <text
            :x="TRACK_X + TRACK_W / 2"
            :y="rowY(i) + filaH(fila.id) + ROW_GAP + CORRIDOIO_INNER_H / 2"
            text-anchor="middle"
            dominant-baseline="middle"
            class="corridoio-label"
          >corridoio</text>
        </template>

        <!-- Hit area cliccabile (sopra tutto) -->
        <rect
          :x="FRAME_X"
          :y="rowY(i)"
          :width="FRAME_W"
          :height="filaH(fila.id)"
          fill="transparent"
          class="fila-hit"
          style="cursor: pointer;"
          @click="$emit('fila-selezionata', fila)"
          @mouseenter="hover_fila_id = fila.id"
          @mouseleave="hover_fila_id = null"
        />
      </g>

    </svg>
  </div>
  </template>

  <!-- ===== Orientamento verticale (mobile) ===== -->
  <div v-else class="serra-mappa-wrapper">
    <svg
      :viewBox="`0 0 ${V_SVG_W} ${V_FIXED_H}`"
      width="100%"
      class="serra-mappa-svg"
    >
      <defs>
        <pattern
          :id="`grid-${serra.id}`"
          x="0" y="0" width="7" height="7"
          patternUnits="userSpaceOnUse"
        >
          <path d="M 7 0 L 0 0 0 7" fill="none"
                :stroke="tipo_colori.stroke" stroke-width="1" stroke-opacity="0.22"/>
        </pattern>
        <pattern
          v-for="info in pattern_list"
          :key="info.pattern_id"
          :id="info.pattern_id"
          x="0" y="0" width="1" height="1"
          patternContentUnits="objectBoundingBox"
        >
          <image
            :href="info.img_url"
            x="0" y="0" width="1" height="1"
            preserveAspectRatio="xMidYMid slice"
          />
        </pattern>
        <clipPath
          v-for="sec in pie_sector_list"
          :key="sec.cp_id"
          :id="sec.cp_id"
          clipPathUnits="objectBoundingBox"
        >
          <path :d="sec.path_d"/>
        </clipPath>
        <pattern
          v-for="pie in pie_pattern_list"
          :key="pie.pattern_id"
          :id="pie.pattern_id"
          x="0" y="0" width="1" height="1"
          patternContentUnits="objectBoundingBox"
        >
          <g v-for="sec in pie.sectors" :key="`sm-${sec.cp_id}`">
            <image v-if="sec.img_url" :href="sec.img_url" x="0" y="0" width="1" height="1"
                   :clip-path="`url(#${sec.cp_id})`" preserveAspectRatio="xMidYMid slice"/>
            <path v-else :d="sec.path_d" :fill="sec.fallback"/>
          </g>
        </pattern>
      </defs>

      <!-- Sfondo cornice -->
      <rect
        v-if="file_ordinate.length"
        :x="V_FRAME_X" :y="V_FRAME_Y"
        :width="V_FRAME_W" :height="V_FRAME_H"
        :fill="tipo_colori.fill" :stroke="tipo_colori.stroke"
        stroke-width="2" rx="8"
      />
      <rect
        v-if="file_ordinate.length"
        :x="V_FRAME_X" :y="V_FRAME_Y"
        :width="V_FRAME_W" :height="V_FRAME_H"
        :fill="`url(#grid-${serra.id})`"
        rx="8" pointer-events="none"
      />

      <!-- Avanserra (striscia orizzontale in fondo alla cornice, sempre visibile) -->
      <g v-if="file_ordinate.length">
        <rect
          :x="V_FRAME_X + 1" :y="V_FRAME_Y + V_FRAME_H - V_AVANSERRA_H + 1"
          :width="V_FRAME_W - 2" :height="V_AVANSERRA_H - 2"
          fill="#D5C9A8"
        />
        <text
          :x="V_FRAME_X + V_FRAME_W / 2"
          :y="V_FRAME_Y + V_FRAME_H - V_AVANSERRA_H / 2"
          text-anchor="middle" dominant-baseline="middle"
          class="avanserra-label"
        >avanserra</text>
      </g>

      <!-- File (colonne verticali) -->
      <g v-for="(fila, i) in file_ordinate" :key="'col-' + fila.id">
        <!-- Etichetta numero fila (sopra la cornice) -->
        <text
          :x="colCenterX(i)"
          :y="V_FRAME_Y - 4"
          text-anchor="middle"
          dominant-baseline="auto"
          class="fila-label"
          :style="{ fill: tipo_colori.stroke }"
        >{{ fila.numero }}</text>

        <!-- Corridoio (strip verticale dopo questa colonna) -->
        <rect
          v-if="fila.corridoio_dopo && i < file_ordinate.length - 1"
          :x="col_positions[i] + V_COL_W + Math.floor(V_COL_GAP / 2)"
          :y="V_FRAME_Y + 1"
          :width="V_CORRIDOIO_W"
          :height="V_FRAME_H - 2"
          fill="#D5C9A8"
        />

        <!-- Vasi (dall'alto in basso) -->
        <circle
          v-for="vi in fila.num_vasi"
          :key="'vv-' + vi"
          :cx="colCenterX(i)"
          :cy="potCyV(fila, vi)"
          :r="V_pot_radius"
          :fill="cellPatternId(fila.id, vi) ? `url(#${cellPatternId(fila.id, vi)})` : cellColor(fila.id, vi)"
          :stroke="cellStroke(fila.id, vi)"
          :stroke-width="V_pot_stroke_w"
        />

        <!-- Contatore vasi (sotto la cornice) -->
        <text
          :x="colCenterX(i)"
          :y="V_FRAME_Y + V_FRAME_H + V_COUNTER_H / 2"
          text-anchor="middle"
          dominant-baseline="middle"
          class="vasi-count"
        >{{ fila.num_vasi }}</text>

        <!-- Hit area per click su fila -->
        <rect
          :x="col_positions[i]"
          :y="V_FRAME_Y"
          :width="V_COL_W"
          :height="V_FRAME_H"
          fill="transparent"
          class="fila-hit"
          style="cursor: pointer;"
          @click="$emit('fila-selezionata', fila)"
        />
      </g>
    </svg>
  </div>

    <!-- Stato vuoto -->
    <div v-if="!file_ordinate.length" class="empty-state text-center pa-10">
      <v-icon size="48" color="grey lighten-2">mdi-view-sequential-outline</v-icon>
      <div class="text-body-2 grey--text mt-3 mb-4">Nessuna fila configurata</div>
      <v-btn outlined small color="primary" @click="$emit('modifica-serra')">
        <v-icon small class="mr-1">mdi-pencil</v-icon>
        Configura file
      </v-btn>
    </div>

    <!-- Tooltip hover fila (solo desktop) -->
    <div
      v-if="!is_vertical && hover_recap"
      class="fila-tooltip"
      :style="{ left: hover_mouse.x + 'px', top: hover_mouse.y + 'px' }"
    >
      <div class="caption font-weight-bold grey--text text--darken-1 mb-1">
        Fila {{ hover_fila_nome }}
      </div>
      <div
        v-for="item in hover_recap.righe"
        :key="item.id"
        class="d-flex align-start tooltip-row"
      >
        <template v-if="!item.component_infos || item.component_infos.length <= 1">
          <img v-if="item.img_url" :src="item.img_url" class="tooltip-swatch mr-2 flex-shrink-0" />
          <div v-else class="tooltip-swatch mr-2 flex-shrink-0" :style="{ backgroundColor: item.color }" />
        </template>
        <svg v-else class="tooltip-swatch mr-2 flex-shrink-0" viewBox="0 0 20 20" width="20" height="20">
          <defs>
            <clipPath
              v-for="(inf, idx) in item.component_infos"
              :key="`cptt-${item.id}-${idx}`"
              :id="`ptt-${item.id}-${idx}`"
            >
              <path :d="_sectorPath(idx, item.component_infos.length, 10, 10, 10)"/>
            </clipPath>
          </defs>
          <g v-for="(inf, idx) in item.component_infos" :key="`sectt-${item.id}-${idx}`">
            <image v-if="inf.img_url" :href="inf.img_url" x="0" y="0" width="20" height="20"
                   :clip-path="`url(#ptt-${item.id}-${idx})`" preserveAspectRatio="xMidYMid slice"/>
            <path v-else :d="_sectorPath(idx, item.component_infos.length, 10, 10, 10)"
                  :fill="inf.fallback"/>
          </g>
          <circle cx="10" cy="10" r="9.5" fill="none" stroke="rgba(0,0,0,0.1)" stroke-width="1"/>
        </svg>
        <div class="flex-grow-1" style="line-height:1.3; min-width:0">
          <div v-if="item.tipo_contenitore" class="text-caption grey--text text--darken-1" style="line-height:1;margin-bottom:1px">{{ item.tipo_contenitore }}</div>
          <div v-if="item.specie_nomi" class="text-caption grey--text font-italic" style="line-height:1">{{ item.specie_nomi }}</div>
          <div v-for="(riga, ri) in item.righe_dettaglio" :key="ri" class="text-caption tooltip-label d-flex align-center" style="gap:4px">
            <span
              :title="riga.hex || 'Nessun HEX'"
              :style="{
                display: 'inline-block',
                width: '10px', height: '10px',
                borderRadius: '2px',
                border: '1px solid rgba(0,0,0,0.18)',
                flexShrink: 0,
                background: riga.hex
                  ? riga.hex
                  : 'linear-gradient(to bottom right, #fff 0%, #fff calc(50% - 0.8px), #e53935 calc(50% - 0.8px), #e53935 calc(50% + 0.8px), #fff calc(50% + 0.8px), #fff 100%)',
              }"
            />
            {{ riga.testo }}
          </div>
          <div v-if="!item.righe_dettaglio || !item.righe_dettaglio.length" class="text-caption tooltip-label">{{ item.label }}</div>
        </div>
        <div class="d-flex flex-column align-end ml-2 flex-shrink-0" style="white-space:nowrap">
          <span class="text-caption grey--text">{{ item.diametro ? `Ø${item.diametro}` : '—' }}</span>
          <span class="text-caption font-weight-medium">{{ item.vasi }}v</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import colorUtility from "../Mixin/colorUtility.js";
import specieLookup from "../Mixin/specieLookup.js";

const TIPI_SERRA = {
  ferrovetro:   { fill: '#E3F2FD', stroke: '#1565C0' },
  film:         { fill: '#FFF8E1', stroke: '#E65100' },
  ombreggiante: { fill: '#EDE7F6', stroke: '#4527A0' },
  campo_aperto: { fill: '#E8F5E9', stroke: '#2E7D32' },
};
const TIPO_FALLBACK = { fill: '#ECEFF1', stroke: '#546E7A' };

const PALETTE = [
  "#4CAF50", "#2196F3", "#FF9800", "#E91E63", "#9C27B0",
  "#00BCD4", "#FF5722", "#795548", "#009688", "#3F51B5",
];

export default {
  name: "SerraMappaView",
  mixins: [colorUtility, specieLookup],
  props: {
    serra: { type: Object, required: true },
    tipo: { type: String, default: null },
    specie: { type: Array, default: () => [] },
    snapshot: { type: Array, default: () => [] },
    prodotti_pianificazione: { type: Array, default: () => [] },
    prodotto_labels: { type: Object, default: () => ({}) },
    contenitori: { type: Array, default: () => [] },
    file_stagione: { type: Array, default: () => [] },
    svg_width: { type: Number, default: 960 },
    show_avanserra: { type: Boolean, default: true },
    orientation: { type: String, default: 'horizontal' },
  },
  data() {
    return {
      hover_fila_id: null,
      hover_mouse:   { x: 0, y: 0 },
      // layout orizzontale (desktop)
      LABEL_W: 38,
      FRAME_PAD: 12,
      ROW_GAP: 6,
      CORRIDOIO_H: 28,
      PAD_Y: 20,
      COUNTER_W: 32,
      // layout verticale (mobile)
      V_COL_W: 34,
      V_COL_GAP: 6,
      V_CORRIDOIO_W: 16,
      V_AVANSERRA_H: 22,
      V_LABEL_H: 18,
      V_COUNTER_H: 16,
      V_FRAME_PAD: 8,
      V_PAD_X: 10,
    };
  },
  computed: {
    SVG_W() { return this.svg_width; },
    AVANSERRA_W() { return this.show_avanserra ? 30 : 0; },
    is_vertical() { return this.orientation === 'vertical'; },

    /* ---- layout verticale ---- */
    V_FIXED_H() {
      const overhead = this.PAD_Y * 2 + this.V_LABEL_H + this.V_COUNTER_H + this.V_FRAME_PAD * 2 + this.V_AVANSERRA_H;
      // step minimo = V_COL_W → i vasi restano circolari (step ≥ diametro massimo)
      return Math.max(600, overhead + this.V_COL_W * this.max_num_vasi);
    },
    col_positions() {
      if (!this.file_ordinate.length) return [];
      const positions = [];
      let x = this.V_PAD_X;
      for (let i = 0; i < this.file_ordinate.length; i++) {
        positions.push(x);
        if (i < this.file_ordinate.length - 1) {
          x += this.V_COL_W + this.V_COL_GAP;
          if (this.file_ordinate[i].corridoio_dopo) x += this.V_CORRIDOIO_W;
        }
      }
      return positions;
    },
    V_SVG_W() {
      if (!this.col_positions.length) return 100;
      return this.col_positions[this.col_positions.length - 1] + this.V_COL_W + this.V_PAD_X;
    },
    V_FRAME_X() { return this.V_PAD_X; },
    V_FRAME_Y() { return this.PAD_Y + this.V_LABEL_H; },
    V_FRAME_W() { return this.V_SVG_W - this.V_PAD_X * 2; },
    V_FRAME_H() {
      return this.V_FIXED_H - this.PAD_Y * 2 - this.V_LABEL_H - this.V_COUNTER_H;
    },
    V_TRACK_Y() {
      // avanserra è sempre in fondo in modalità verticale → nessun offset in cima
      return this.V_FRAME_Y + this.V_FRAME_PAD;
    },
    V_TRACK_H() {
      // riserva sempre spazio per l'avanserra in fondo
      return this.V_FRAME_H - this.V_FRAME_PAD * 2 - this.V_AVANSERRA_H;
    },
    V_pot_size() {
      const step = this.V_TRACK_H / Math.max(1, this.max_num_vasi);
      return Math.min(this.V_COL_W - 4, Math.max(2, step * 0.88));
    },
    V_pot_radius() { return this.V_pot_size / 2; },
    V_pot_stroke_w() { return this.V_pot_size >= 6 ? 0.7 : 0.3; },

    /* ---- specie (richiesto da specieLookup) ---- */
    specie_disponibili() {
      return this.specie;
    },

    /* ---- colori prodotti (pattern SVG) ---- */
    colori_prodotti() {
      const map = {};
      const ids = [...new Set(this.snapshot.map((p) => p.id_prodotto_pianificazione))];
      for (const id_prod of ids) {
        const prodotto = this.prodotto_map[id_prod];
        const infos = prodotto ? this._componenteInfos(prodotto) : [];
        if (infos.length > 1) {
          map[id_prod] = { pattern_id: `pie_pat_${id_prod}`, img_url: null, use_pattern: true };
        } else {
          const img_url = prodotto ? this._varietaImgUrl(prodotto) : null;
          map[id_prod] = { pattern_id: `vcp_${id_prod}`, img_url, use_pattern: !!img_url };
        }
      }
      return map;
    },
    pattern_list() {
      return Object.values(this.colori_prodotti).filter((info) => info.img_url);
    },
    pie_pattern_list() {
      const result = [];
      const ids = [...new Set(this.snapshot.map(p => p.id_prodotto_pianificazione))];
      for (const id_prod of ids) {
        const prodotto = this.prodotto_map[id_prod];
        if (!prodotto) continue;
        const infos = this._componenteInfos(prodotto);
        if (infos.length <= 1) continue;
        const n = infos.length;
        result.push({
          pattern_id: `pie_pat_${id_prod}`,
          sectors: infos.map((inf, i) => ({
            cp_id: `pie_cp_${id_prod}_${i}`,
            path_d: this._sectorPath(i, n, 0.5, 0.5, 0.5),
            img_url: inf.img_url,
            fallback: inf.fallback,
          })),
        });
      }
      return result;
    },
    pie_sector_list() {
      return this.pie_pattern_list.flatMap(pie => pie.sectors);
    },

    /* ---- colori tipo serra ---- */
    tipo_colori() {
      return TIPI_SERRA[this.tipo] || TIPO_FALLBACK;
    },
    track_fill() {
      const hex = this.tipo_colori.fill;
      if (!hex || !hex.startsWith('#')) return '#E2EAE2';
      const r = Math.max(0, parseInt(hex.slice(1, 3), 16) - 20);
      const g = Math.max(0, parseInt(hex.slice(3, 5), 16) - 20);
      const b = Math.max(0, parseInt(hex.slice(5, 7), 16) - 20);
      return `rgb(${r},${g},${b})`;
    },
    /* ---- layout ---- */
    FRAME_X() { return this.LABEL_W + 4; },
    FRAME_W() { return this.SVG_W - this.FRAME_X - this.COUNTER_W - 8; },
    AVANSERRA_X() { return this.FRAME_X + this.FRAME_PAD; },
    TRACK_X() { return this.FRAME_X + this.FRAME_PAD + this.AVANSERRA_W; },
    TRACK_W() { return this.FRAME_W - this.FRAME_PAD * 2 - this.AVANSERRA_W; },

    CORRIDOIO_INNER_H() { return this.CORRIDOIO_H - this.ROW_GAP - 2; },
    avanserra_strip_h() {
      const n = this.file_ordinate.length;
      if (!n) return 0;
      const corridoi = this.file_ordinate.filter((f) => f.corridoio_dopo).length;
      const total_h = this.file_ordinate.reduce((acc, f) => acc + this.filaH(f.id), 0);
      return total_h + (n - 1) * this.ROW_GAP + corridoi * this.CORRIDOIO_H;
    },

    /* ---- file ---- */
    file_ordinate() {
      return [...this.file_stagione].sort((a, b) => a.numero - b.numero);
    },

    /* ---- dimensionamento vasi ---- */
    max_num_vasi() {
      if (!this.file_ordinate.length) return 1;
      return Math.max(...this.file_ordinate.map((f) => f.num_vasi));
    },
    pot_size() {
      // Diametro del vaso in px, adattato alla riga più densa
      const step = this.TRACK_W / this.max_num_vasi;
      return Math.min(14, Math.max(3, step * 0.82));
    },
    pot_radius() { return this.pot_size / 2; },
    pot_stroke_w() { return this.pot_size >= 6 ? 0.7 : 0.3; },

    ROW_H() {
      // Altezza riga = almeno 18px, al massimo pot_size + 10
      return Math.max(18, this.pot_size + 8);
    },

    /* ---- altezze SVG ---- */
    svgHeight() {
      const n = this.file_ordinate.length;
      if (!n) return 80;
      const corridoi = this.file_ordinate.filter((f) => f.corridoio_dopo).length;
      const total_h = this.file_ordinate.reduce((acc, f) => acc + this.filaH(f.id), 0);
      return (
        this.PAD_Y * 2 +
        this.FRAME_PAD +
        total_h +
        (n - 1) * this.ROW_GAP +
        corridoi * this.CORRIDOIO_H
      );
    },
    frame_height() {
      return this.svgHeight - this.PAD_Y * 2;
    },
    avanserra_path_d() {
      const x = this.FRAME_X + 1;
      const y = this.PAD_Y + 1;
      const w = this.FRAME_PAD + this.AVANSERRA_W - 1;
      const h = this.frame_height - 2;
      const r = 8;
      return `M ${x + r},${y} L ${x + w},${y} L ${x + w},${y + h} L ${x + r},${y + h} Q ${x},${y + h} ${x},${y + h - r} L ${x},${y + r} Q ${x},${y} ${x + r},${y} Z`;
    },

    /* ---- diametro per fila (da snapshot + contenitori) ---- */
    diametro_per_fila() {
      const map = {};
      for (const pos of this.snapshot) {
        if (map[pos.id_fila_stagione] !== undefined) continue;
        const prod = this.prodotto_map[pos.id_prodotto_pianificazione];
        if (!prod) continue;
        const cont = this.contenitori.find((c) => c.id === prod.id_contenitore);
        if (cont) map[pos.id_fila_stagione] = cont.diametro;
      }
      return map;
    },
    diam_range() {
      const vals = Object.values(this.diametro_per_fila).filter(Boolean);
      if (!vals.length) return null;
      return { min: Math.min(...vals), max: Math.max(...vals) };
    },

    /* ---- hover fila tooltip ---- */
    hover_fila_nome() {
      if (!this.hover_fila_id) return '';
      const f = this.file_ordinate.find((f) => f.id === this.hover_fila_id);
      return f ? f.numero : '';
    },
    hover_recap() {
      if (!this.hover_fila_id) return null;
      const posizioni = this.snapshot.filter((p) => p.id_fila_stagione === this.hover_fila_id);
      if (!posizioni.length) return null;
      const fila = this.file_ordinate.find((f) => f.id === this.hover_fila_id);
      const totale_vasi = fila ? fila.num_vasi : 0;
      const ids_order = [];
      const per_prodotto = {};
      for (const pos of posizioni) {
        const id = pos.id_prodotto_pianificazione;
        if (per_prodotto[id] === undefined) { per_prodotto[id] = 0; ids_order.push(id); }
        per_prodotto[id] += pos.vaso_fine - pos.vaso_inizio + 1;
      }
      const righe = ids_order.map((id) => {
        const prodotto = this.prodotto_map[id] || null;
        const vasi = per_prodotto[id];
        const cont = prodotto ? this.contenitori.find((c) => c.id === prodotto.id_contenitore) : null;
        const riepilogo = this._componenteRiepilogo(prodotto, cont);
        return {
          id,
          label: (this.prodotto_labels[id] || `P${id}`).replace(/\s*Ø\d+.*$/, ''),
          img_url: prodotto ? this._varietaImgUrl(prodotto) : null,
          color: this._productColor(prodotto),
          component_infos: this._componenteInfos(prodotto),
          tipo_contenitore: riepilogo.tipo_contenitore,
          specie_nomi: riepilogo.specie_nomi,
          righe_dettaglio: riepilogo.righe,
          diametro: cont ? cont.diametro : null,
          vasi,
        };
      });
      return { righe, totale_vasi };
    },

    /* ---- mappa colori precomputata ---- */
    prodotto_map() {
      const map = {};
      for (const p of this.prodotti_pianificazione) map[p.id] = p;
      return map;
    },
    mappa_colori() {
      const result = {};
      for (const fila of this.file_ordinate) {
        const cells = {}; // sparse map: vaso_index → { color, stroke, tooltip }
        const posizioni = this.snapshot.filter((p) => p.id_fila_stagione === fila.id);
        for (const pos of posizioni) {
          const prodotto = this.prodotto_map[pos.id_prodotto_pianificazione] || null;
          const color = this._productColor(prodotto);
          const info = this.colori_prodotti[pos.id_prodotto_pianificazione];
          const pattern_id = info?.use_pattern ? info.pattern_id : null;
          const stroke = pattern_id ? 'rgba(0,0,0,0.25)' : this._darkenHex(color);
          const label =
            this.prodotto_labels[pos.id_prodotto_pianificazione] ||
            `P${pos.id_prodotto_pianificazione}`;
          const tooltip = `${label} — vasi ${pos.vaso_inizio}–${pos.vaso_fine}`;
          for (let v = pos.vaso_inizio; v <= pos.vaso_fine; v++) {
            cells[v] = { color, stroke, pattern_id, tooltip };
          }
        }
        result[fila.id] = cells;
      }
      return result;
    },
  },
  methods: {
    /* ---- layout verticale ---- */
    colCenterX(i) {
      return (this.col_positions[i] || 0) + this.V_COL_W / 2;
    },
    potCyV(fila, vi) {
      const step = this.V_TRACK_H / fila.num_vasi;
      // vaso 1 in fondo (bottom-left), vaso max in cima
      return this.V_TRACK_Y + (fila.num_vasi - vi + 0.5) * step;
    },

    /* ---- hover tracking ---- */
    onSvgMouseMove(event) {
      const outer = this.$refs.outer_wrapper;
      if (!outer) return;
      const rect = outer.getBoundingClientRect();
      this.hover_mouse = { x: event.clientX - rect.left + 16, y: event.clientY - rect.top + 16 };
    },

    /* ---- posizioni ---- */
    rowY(i) {
      let y = this.PAD_Y + this.FRAME_PAD / 2;
      for (let j = 0; j < i; j++) {
        const f = this.file_ordinate[j];
        y += this.filaH(f.id) + this.ROW_GAP;
        if (f && f.corridoio_dopo) y += this.CORRIDOIO_H;
      }
      return y;
    },
    potCx(fila, vi) {
      const step = this.TRACK_W / fila.num_vasi;
      return this.TRACK_X + (vi - 0.5) * step;
    },
    filaH(fila_id) {
      const range = this.diam_range;
      if (!range || range.min === range.max) return this.ROW_H;
      const diam = this.diametro_per_fila[fila_id];
      if (!diam) return this.ROW_H;
      return Math.round(this.ROW_H * (diam / range.max));
    },
    potRadius(fila) {
      const h_r = Math.max(2, this.filaH(fila.id) / 2 - this.pot_stroke_w - 1);
      const step = this.TRACK_W / fila.num_vasi;
      const spacing_r = step / 2 - this.pot_stroke_w - 0.5;
      return Math.min(h_r, spacing_r);
    },

    /* ---- accessori colore (sparse map) ---- */
    cellColor(fila_id, vi) {
      return (this.mappa_colori[fila_id] && this.mappa_colori[fila_id][vi])
        ? this.mappa_colori[fila_id][vi].color
        : "#D8D8D8";
    },
    cellStroke(fila_id, vi) {
      return (this.mappa_colori[fila_id] && this.mappa_colori[fila_id][vi])
        ? this.mappa_colori[fila_id][vi].stroke
        : "#BBBBBB";
    },
    cellTooltip(fila_id, vi) {
      return (this.mappa_colori[fila_id] && this.mappa_colori[fila_id][vi])
        ? this.mappa_colori[fila_id][vi].tooltip
        : "";
    },
    cellPatternId(fila_id, vi) {
      return (this.mappa_colori[fila_id] && this.mappa_colori[fila_id][vi])
        ? this.mappa_colori[fila_id][vi].pattern_id
        : null;
    },
    _componenteInfos(prodotto) {
      const seen = new Set();
      const fallbacks = ['#EF9A9A', '#90CAF9', '#A5D6A7', '#FFE082'];
      const infos = [];
      for (const c of (prodotto?.componenti || [])) {
        const id_v = (c.id_varieta && c.id_varieta !== 'None') ? c.id_varieta : null;
        const key = `${c.id_specie}|${id_v ?? ''}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const sp = this.specie_disponibili.find(s => s.id == c.id_specie);
        let img_url = null;
        if (sp && id_v) {
          const v = (sp.variety || []).find(v => v.id == id_v);
          if (v?.id_colore) img_url = `${process.env.VUE_APP_API_ROOT}/specie/img_colore/${v.id_colore}?t=${v.md5_colore || ''}`;
        }
        infos.push({ img_url, fallback: fallbacks[infos.length % fallbacks.length] });
        if (infos.length >= 4) break;
      }
      return infos;
    },
    _componenteRiepilogo(prodotto, cont) {
      const seen = new Set();
      const seen_specie = new Set();
      const righe = [];
      for (const c of (prodotto?.componenti || [])) {
        const id_v = (c.id_varieta && c.id_varieta !== 'None') ? c.id_varieta : null;
        const id_s = (c.id_serie  && c.id_serie  !== 'None') ? c.id_serie  : null;
        const key = `${c.id_specie}|${id_s ?? ''}|${id_v ?? ''}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const sp = this.specie_disponibili.find(s => s.id == c.id_specie);
        if (sp?.nome) seen_specie.add(sp.nome);
        const ns = id_s ? this.nomeSerie(c.id_specie, id_s)   : null;
        const nv = id_v ? this.nomeVarieta(c.id_specie, id_v) : null;
        const v  = (id_v && sp) ? (sp.variety || []).find(vv => vv.id == id_v) : null;
        const hex = v?.id_colore ? this.$store.getters.getHexByColoreId(v.id_colore) : null;
        righe.push({ testo: [ns, nv].filter(Boolean).join(' · ') || 'Generico', hex });
        if (righe.length >= 4) break;
      }
      return {
        tipo_contenitore: cont?.tipo || null,
        specie_nomi: [...seen_specie].join(' / ') || null,
        righe,
      };
    },
    _sectorPath(i, n, r, cx, cy) {
      const a0 = (i / n) * 2 * Math.PI;
      const a1 = ((i + 1) / n) * 2 * Math.PI;
      const x0 = cx + r * Math.sin(a0);
      const y0 = cy - r * Math.cos(a0);
      const x1 = cx + r * Math.sin(a1);
      const y1 = cy - r * Math.cos(a1);
      const large = (a1 - a0) > Math.PI ? 1 : 0;
      return `M ${cx},${cy} L ${x0.toFixed(3)},${y0.toFixed(3)} A ${r},${r} 0 ${large},1 ${x1.toFixed(3)},${y1.toFixed(3)} Z`;
    },
    _varietaImgUrl(prodotto) {
      for (const c of (prodotto.componenti || [])) {
        const id_v = (c.id_varieta && c.id_varieta !== 'None') ? c.id_varieta : null;
        if (!id_v) continue;
        const sp = this.specie_disponibili.find((s) => s.id == c.id_specie);
        if (!sp) continue;
        const varieta = (sp.variety || []).find((v) => v.id == id_v);
        if (!varieta || !varieta.id_colore) continue;
        return `${process.env.VUE_APP_API_ROOT}/specie/img_colore/${varieta.id_colore}?t=${varieta.md5_colore || ''}`;
      }
      return null;
    },

    /* ---- colori prodotto ---- */
    _productColor(prodotto) {
      if (!prodotto) return "#D8D8D8";
      if (prodotto.colore_contenitore) {
        const parsed = this.parseColorEntry(prodotto.colore_contenitore);
        if (parsed && parsed.hex) return parsed.hex;
      }
      return PALETTE[(prodotto.id || 0) % PALETTE.length];
    },
    _darkenHex(hex) {
      if (!hex || !hex.startsWith("#")) return "#999";
      const r = Math.max(0, parseInt(hex.slice(1, 3), 16) - 35);
      const g = Math.max(0, parseInt(hex.slice(3, 5), 16) - 35);
      const b = Math.max(0, parseInt(hex.slice(5, 7), 16) - 35);
      return `rgb(${r},${g},${b})`;
    },
  },
};
</script>

<style scoped>
.serra-mappa-wrapper {
  overflow-x: auto;
}
.empty-state {
  min-height: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.serra-mappa-svg {
  display: block;
}
.fila-hit:hover {
  fill: rgba(0, 80, 0, 0.06);
}
.fila-label {
  font-size: 11px;
  font-weight: 600;
  pointer-events: none;
}
.serra-title {
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}
.vasi-count {
  font-size: 9px;
  fill: #8A9E8A;
  pointer-events: none;
}
.corridoio-label {
  font-size: 9px;
  fill: #7A6840;
  font-weight: 500;
  letter-spacing: 0.5px;
  pointer-events: none;
}
.avanserra-label {
  font-size: 8px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  fill: #7A6840;
  pointer-events: none;
}
.fila-tooltip {
  position: absolute;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 8px 12px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.13);
  z-index: 20;
  pointer-events: none;
  min-width: 260px;
  max-width: 380px;
}
.tooltip-row {
  padding: 2px 0;
}
.tooltip-swatch {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  flex-shrink: 0;
  object-fit: cover;
  border: 1px solid rgba(0, 0, 0, 0.1);
}
.tooltip-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 240px;
}
</style>
